import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ButtonBack from "../../utils/ButtonBack";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots
const SEC_COLORS = {
  team: "#2F7FD1",
  members: "#12A08F",
};

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, marginBottom: 14 };
const cardHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${LINE}` };
const cardTitle = { display: "flex", alignItems: "center", gap: 9, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: INK_2 };
const dot = (c) => ({ width: 6, height: 6, flex: "0 0 6px", borderRadius: "50%", background: c });
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const labelStyle = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const inputStyle = (hasError, mono) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: mono ? MONO : FONT,
  fontSize: mono ? 13 : 13.5,
  color: INK,
  outline: "none",
});
const errorStyle = { display: "block", margin: "4px 0 0", fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: ERROR };
const primaryBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: `${PRIMARY} !important`, color: "#FFFFFF !important", border: "0 !important",
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none", whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` }, "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important`,
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none",
  "&:hover": { background: "#F4F7FA !important" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: `repeat(${n}, minmax(0,1fr))` },
});

// Scoped resets so global App.css rules (form, border-container,
// phone-container, inputs, labels) can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-org .border-container,.dc-org form.form,.dc-org .dashboard-container,.dc-org .add-forms{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important;background:transparent!important;border:0!important;box-sizing:border-box!important;}
.dc-org .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-org .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-org .dc-card input:not([type=checkbox]){width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;height:36px!important;font-size:13.5px!important;}
.dc-org .dc-card .MuiInputBase-root input{height:auto!important;width:auto!important;flex:1 1 60px;}
.dc-org .dc-card .MuiAutocomplete-root,.dc-org .dc-card .MuiInputBase-root{width:100%!important;max-width:none!important;}
.dc-org .dc-card .MuiFormControlLabel-root{width:100%!important;}
.dc-org .dc-card .phone-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;}
.dc-org .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-org .dc-card input::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-org .dc-card p.error{margin:4px 0 0!important;}
.dc-org .dc-head-btns{display:flex!important;flex-direction:row!important;margin:0!important;padding:0!important;align-items:center!important;align-self:flex-end!important;}
.dc-org .dc-head-btns .MuiButton-root,.dc-org .dc-head-btns button{margin:0!important;height:38px!important;min-height:38px!important;max-height:38px!important;padding-top:0!important;padding-bottom:0!important;line-height:1!important;position:static!important;inset:auto!important;top:auto!important;bottom:auto!important;transform:none!important;translate:none!important;align-self:center!important;margin-block:0!important;vertical-align:middle!important;box-sizing:border-box!important;}
`;

const EditTeam = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const orgId = JSON.parse(localStorage.getItem("org"));
  const [users, setUsers] = useState([]);
  const [name, setName] = useState();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSelf, setIsSelf] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width: 750px)");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const afterClose = useRef(null);

  const showModal = (msg, type, onClose = null) => {
    setModalMsg(msg);
    setModalType(type);
    afterClose.current = onClose;
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    const next = afterClose.current;
    afterClose.current = null;
    if (next) next();
  };

  useEffect(() => {
    const getOrgUsers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/all?orgId=${orgId}&username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setUsers(res.data);
      } catch (error) {
        console.error(error);
      }
    };

    getOrgUsers();
    const fetchTeam = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/team?teamId=${teamId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setName(res.data.name);
        setSelectedUsers(res.data?.orgUsers?.map((u) => u.mobile));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamId, orgId]);

  const handleUpdate = async () => {
    const newErrors = {};

    if (!isSelf && selectedUsers.length === 0) {
      newErrors.selectedUsers = "Please select at least one user";
    }
    if (!name || name.toString().trim() === "")
      newErrors.name = "Team name is required";
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      setBtnLoading(true)
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/team?teamId=${teamId}`,
        {
          orgId: orgId,
          name: name,
          usernames: selectedUsers,
          type: isSelf ? "Self" : null,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      showModal("Team updated successfully", "success", () =>
        navigate("/dashboard/teams"),
      );
    } catch (error) {
      showModal(error.response?.data?.Exception || "Error updating team", "error");
    } finally {
      setBtnLoading(false)
    }
  };

  if (loading)
    return (
      <p style={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 500, color: MUTED, padding: "24px 0", textAlign: "center" }}>
        Loading team...
      </p>
    );

  const goTeams = () => navigate("/dashboard/teams");

  /* ---------- markup helpers (same handlers as before) ---------- */
  const section = (id, title, meta, children, n = 3) => (
    <section key={id} className="dc-card" style={card}>
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[id])} />
          {title}
        </span>
        <span style={cardMeta}>{meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(n)}>{children}</Box>
      </Box>
    </section>
  );

  const saveButton = (
    <Button
      className="btn-contained"
      onClick={handleUpdate}
      disabled={btnLoading}
      sx={primaryBtn}
    >
      {btnLoading ? "Updating..." : "Update Team"}
    </Button>
  );

  return (
    <>
      <style>{SCOPED_CSS}</style>
      <div
        className="dc-org"
        style={{ fontFamily: FONT, color: INK, textAlign: "left", padding: isMobile ? "4px 0 88px" : "4px 0 32px" }}
      >
        {/* ---------------- PAGE HEADER ---------------- */}
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
          <div style={{ minWidth: 0 }}>
            <button
              type="button"
              onClick={goTeams}
              style={{ border: 0, background: "transparent", padding: 0, marginBottom: 14, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
            >
              ← Teams
            </button>
            <Typography
              component="h1"
              sx={{ m: 0, fontFamily: FONT, fontSize: isMobile ? 24 : 28, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: INK }}
            >
              Edit team
            </Typography>
            <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {name || "Update the team name and members."}
            </div>
          </div>
          {!isMobile && (
            <Box className="dc-head-btns" sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: "0 0 auto" }}>
              <Button className="btn-outlined" onClick={goTeams} sx={ghostBtn}>
                Cancel
              </Button>
              {saveButton}
            </Box>
          )}
        </Box>

        <div className="add-forms">
          {/* ---------------- TEAM ---------------- */}
          {section("team", "Team", "1 required", [
            <Box key="name">
              <label htmlFor="name" style={labelStyle}>
                Team Name<span style={req}>*</span>
              </label>
              <input
                className={`${errors.name && "input-error"}`}
                name="name"
                id="name"
                type="text"
                placeholder="Team name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle(!!errors.name)}
              />
              {errors.name && <span className="error-text" style={errorStyle}>{errors.name}</span>}
            </Box>,
            <Box key="self">
              <label style={labelStyle}>Team type</label>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSelf}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsSelf(checked);

                      if (checked) {
                        // set only self
                        setSelectedUsers([storedUser.mobile]);
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    sx={{
                      p: 0.5,
                      color: "#9AA7B8",
                      "&.Mui-checked": { color: PRIMARY },
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  />
                }
                label="Self"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: 36,
                  boxSizing: "border-box",
                  m: 0,
                  px: 0.75,
                  background: isSelf ? "#EAF2FB" : FIELD_BG,
                  border: `1px solid ${isSelf ? "#8FC3E6" : FIELD_EDGE}`,
                  borderRadius: "8px",
                  transition: "background .15s, border-color .15s",
                  "& .MuiFormControlLabel-label": {
                    fontFamily: FONT,
                    fontSize: 13.5,
                    fontWeight: isSelf ? 600 : 500,
                    color: isSelf ? "#1B4F82" : INK_2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </Box>,
          ])}

          {/* ---------------- MEMBERS ---------------- */}
          {section(
            "members",
            "Members",
            isSelf ? "self team — only you" : `${selectedUsers.length} selected`,
            [
              <Box key="members" sx={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>
                  Select Members<span style={req}>*</span>
                </label>
                <Autocomplete
                  disabled={isSelf}
                  className={`${errors.selectedUsers && "input-error"}`}
                  multiple
                  options={users}
                  getOptionLabel={(option) => `${option.name} (${option.userRoleName})`}
                  isOptionEqualToValue={(option, value) =>
                    option.mobile === value.mobile
                  }
                  value={users.filter((u) => selectedUsers.includes(u.mobile))}
                  onChange={(event, value) =>
                    setSelectedUsers(value.map((v) => v.mobile))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={isSelf ? "Self team — members not needed" : "Select Members"}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          padding: "3px 6px !important",
                          background: FIELD_BG,
                          borderRadius: "8px",
                          fontFamily: FONT,
                          fontSize: "13.5px",
                          minHeight: 36,
                          display: "flex",
                          alignItems: "center",
                          "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
                          "& .MuiOutlinedInput-notchedOutline": {
                            top: 0,
                            border: `1px solid ${errors.selectedUsers ? ERROR : FIELD_EDGE}`,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: `1px solid ${errors.selectedUsers ? ERROR : "#B4C6DA"}`,
                          },
                          "&.Mui-focused": { background: "#FFFFFF" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: `1px solid ${PRIMARY}`,
                          },
                          "&.Mui-disabled": { background: "#F1F4F8" },
                        },
                        "& .MuiAutocomplete-input": {
                          padding: "4px 4px !important",
                          fontSize: "13.5px",
                          height: "auto !important",
                        },
                        "& input::placeholder": { color: "#8A97A8", opacity: 1 },
                        "& .MuiChip-root": {
                          height: 24,
                          borderRadius: "999px",
                          background: "#EAF2FB",
                          border: "1px solid #C9DEF3",
                          color: "#1B4F82",
                          fontFamily: FONT,
                          fontSize: 12.5,
                          fontWeight: 600,
                        },
                        "& .MuiChip-deleteIcon": {
                          fontSize: 16,
                          color: "#1B4F82",
                          opacity: 0.6,
                          "&:hover": { color: "#1B4F82", opacity: 1 },
                        },
                      }}
                    />
                  )}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 0.5,
                        borderRadius: "12px",
                        border: `1px solid ${LINE}`,
                        boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
                        "& .MuiAutocomplete-option": {
                          fontFamily: FONT,
                          fontSize: 13.5,
                          borderRadius: "8px",
                          mx: 0.75,
                        },
                      },
                    },
                  }}
                  sx={{ my: 0 }}
                />
                {errors.selectedUsers && (
                  <span className="error-text" style={errorStyle}>{errors.selectedUsers}</span>
                )}
              </Box>,
            ],
          )}
        </div>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 1.25,
              p: "10px 16px", background: "#FFFFFF", borderTop: `1px solid ${LINE}`, boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={goTeams} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>{saveButton}</Box>
          </Box>
        )}

        <ErrorModal
          open={modalOpen}
          handleClose={handleModalClose}
          msg={modalMsg}
          type={modalType}
        />
      </div>
    </>
  );
};

export default EditTeam;